import { useEffect } from 'react';
import { MediumFormRow, BigFormRow } from 'framework/ui/layout';
import { ClearAfterUnmountWrapper, EnumDropdown, Checkbox, CountryDropdown, useFormContext } from 'framework/form';
import { OfficeDropdown, PositionDropdown, EmployeeContractTypeDropdown } from 'domain/shared/employee';
import ComplianceDocumentTypeSuggestionInput from '../inputs/ComplianceDocumentTypeSuggestionInput';
import {
  GRACE_PERIOD_DAYS,
  COMPLIANCE_DOCUMENT_STATUS,
  COMPLIANCE_DOCUMENT_TYPE_VALUES,
  ComplianceDocumentFormType,
} from 'domain/shared/compliance';
import {
  isAllEmployeesAvailable,
  isAudienceFiltersVisible,
  isInformationalDocument,
} from './complianceDocumentFormFields.utils';
import { ArtifactUploaderInput, MAX_SIZE } from 'domain/shared/artifact/input';
import { Box, Typography } from '@mui/material';
import { LoadProjectsDropdown } from 'domain/shared/newProject';

type ComplianceDocumentFormFieldsProps = {
  isEditForm: boolean;
  showDocumentMetaFields?: boolean;
  showUploader?: boolean;
};

const ComplianceDocumentFormFields = ({
  isEditForm,
  showDocumentMetaFields = true,
  showUploader = true,
}: ComplianceDocumentFormFieldsProps) => {
  const { data = {}, onChangeCallback } = useFormContext();
  const isInformationalDocumentType = isInformationalDocument(data?.complianceType);

  useEffect(() => {
    if (!onChangeCallback) {
      return;
    }

    const nextData: Partial<ComplianceDocumentFormType> = {};

    if (!isInformationalDocumentType && data?.isSignatureRequired !== true) {
      nextData.isSignatureRequired = true;
    }

    if (isInformationalDocumentType && data?.isSignatureRequired == null) {
      nextData.isSignatureRequired = true;
    }

    if (data?.isSignatureRequired === false && data?.gracePeriodDays !== null) {
      nextData.gracePeriodDays = null;
    }

    if (Object.keys(nextData).length) {
      onChangeCallback(nextData);
    }
  }, [
    data?.complianceType,
    data?.gracePeriodDays,
    data?.isSignatureRequired,
    isInformationalDocumentType,
    onChangeCallback,
  ]);

  return (
    <>
      {showDocumentMetaFields && (
        <MediumFormRow>
          <ComplianceDocumentTypeSuggestionInput
            source="complianceName"
            label=""
            placeholder="Name"
            disabled={isEditForm}
          />
          <EnumDropdown options={COMPLIANCE_DOCUMENT_TYPE_VALUES} source="complianceType" label="" placeholder="Type" />
        </MediumFormRow>
      )}

      <Box mb={2}>
        <Typography variant="body1" color="primary">
          Target audience
        </Typography>
      </Box>

      <MediumFormRow>
        <ClearAfterUnmountWrapper isVisible={formData => isAllEmployeesAvailable(formData?.complianceType)}>
          <Checkbox source="isAllEmployee" label="To all workers" />
        </ClearAfterUnmountWrapper>

        {isInformationalDocumentType && (
          <Checkbox source="isSignatureRequired" label="Signature required" defaultValue={true} />
        )}
      </MediumFormRow>

      <MediumFormRow>
        <ClearAfterUnmountWrapper isVisible={isAudienceFiltersVisible}>
          <PositionDropdown source="positionIds" label="" placeholder="Position(s)" multiple />
        </ClearAfterUnmountWrapper>

        <ClearAfterUnmountWrapper isVisible={isAudienceFiltersVisible}>
          <OfficeDropdown source="offices" label="" placeholder="Office(s)" multiple />
        </ClearAfterUnmountWrapper>
      </MediumFormRow>

      <MediumFormRow>
        <ClearAfterUnmountWrapper isVisible={isAudienceFiltersVisible}>
          <EmployeeContractTypeDropdown
            source="relatedEmployeeContractTypes"
            label=""
            placeholder="Contract type(s)"
            multiple
          />
        </ClearAfterUnmountWrapper>

        <ClearAfterUnmountWrapper isVisible={isAudienceFiltersVisible}>
          <CountryDropdown
            source="businessRegistrationCountries"
            label=""
            placeholder="Business registration country"
            multiple
          />
        </ClearAfterUnmountWrapper>
      </MediumFormRow>

      <MediumFormRow>
        <ClearAfterUnmountWrapper isVisible={isAudienceFiltersVisible}>
          <LoadProjectsDropdown source="assignments" label="" placeholder="Assignments" />
        </ClearAfterUnmountWrapper>
      </MediumFormRow>

      <MediumFormRow>
        {data?.isSignatureRequired && (
          <ClearAfterUnmountWrapper>
            <EnumDropdown options={GRACE_PERIOD_DAYS} source="gracePeriodDays" label="" placeholder="Grace period" />
          </ClearAfterUnmountWrapper>
        )}
      </MediumFormRow>

      {showUploader && (
        <BigFormRow>
          <ArtifactUploaderInput
            fileExtensions="md"
            isVisible={(formData: ComplianceDocumentFormType) =>
              !isEditForm || (formData?.status === COMPLIANCE_DOCUMENT_STATUS.UNPUBLISHED && !formData?.isPolicySigned)
            }
            isNewDocumentStorage
            maximumSize={MAX_SIZE}
            label=""
            source="paths"
          />
        </BigFormRow>
      )}
    </>
  );
};

export default ComplianceDocumentFormFields;
